/**
 * Parallel Qobuz + local library search (v1 — no deduplication).
 */

import {
  searchService,
  type SearchTrack,
  type UnifiedSearchResult,
} from './searchService';
import { normalizeLocalSearchTrack } from './unifiedSearchNormalize';
import type { CatalogSearchTrack, CatalogSource } from './unifiedSearchTypes';
export type { CatalogSearchTrack, CatalogSource };
import {
  EMPTY_LOCAL_CATALOG,
  fetchLocalSearchIfConfigured,
  hasLocalCatalogForSearch,
  localCatalogResultCount,
  searchLocalCatalog,
  type CatalogSearchAlbum,
  type CatalogSearchArtist,
  type LocalCatalogSearchResult,
} from './unifiedSearchLocal';

export type { CatalogSearchArtist, CatalogSearchAlbum, LocalCatalogSearchResult };
export { normalizeLocalSearchTrack };

export interface ParallelCatalogSearchResult {
  qobuz: UnifiedSearchResult | null;
  qobuzError: string | null;
  local: LocalCatalogSearchResult;
  localTracks: CatalogSearchTrack[];
}

export const QOBUZ_SEARCH_ERROR_MESSAGE =
  'Qobuz search failed. Check your connection or sign in to Qobuz.';

export {
  hasLocalCatalogForSearch,
  searchLocalCatalog,
  localCatalogResultCount,
  fetchLocalSearchIfConfigured,
  EMPTY_LOCAL_CATALOG,
};

export { invalidateLocalLibrarySourcesCache, hasLocalLibrarySourcesConfigured } from './unifiedSearchSources';

export function normalizeQobuzSearchTrack(track: SearchTrack): CatalogSearchTrack {
  return {
    id: track.id,
    title: track.title,
    artist: track.artistName,
    album: track.albumTitle,
    duration: track.duration || 0,
    source: 'qobuz',
    artistName: track.artistName,
    albumTitle: track.albumTitle,
    coverUrl: track.coverUrl,
    sourceLabel: track.sourceLabel || 'Qobuz',
    isPreview: track.isPreview,
    maxBitDepth: track.maxBitDepth,
    maxSampleRate: track.maxSampleRate,
    serviceTrackId: track.serviceTrackId,
  };
}

export async function runParallelCatalogSearch(
  query: string,
  qobuzLimit = 15,
  localLimit = 10,
): Promise<ParallelCatalogSearchResult> {
  const trimmed = (query || '').trim();
  const hasLocal = await hasLocalCatalogForSearch();

  const qobuzPromise = searchService
    .unified(trimmed, qobuzLimit)
    .then((data) => ({ data, error: null as string | null }))
    .catch((err: unknown) => ({
      data: null,
      error: err instanceof Error ? err.message : 'Search failed. Please try again.',
    }));

  const localPromise: Promise<LocalCatalogSearchResult> = hasLocal
    ? searchLocalCatalog(trimmed, localLimit)
    : Promise.resolve({ ...EMPTY_LOCAL_CATALOG });

  const [qobuzOutcome, local] = await Promise.all([qobuzPromise, localPromise]);

  return {
    qobuz: qobuzOutcome.data,
    qobuzError: qobuzOutcome.error,
    local,
    localTracks: local.tracks,
  };
}

export async function searchLocalCatalogTracks(
  query: string,
  limit: number,
): Promise<CatalogSearchTrack[]> {
  const local = await searchLocalCatalog(query, limit);
  return local.tracks;
}
